import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import { siteInfo } from "@/mocks/siteInfo";

export default function AboutPage() {
  const pageTitle = `关于我们 - ${siteInfo.name}`;
  const pageUrl = `${siteInfo.url}/about`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "国模白露写真馆是一个什么样的平台？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "国模白露写真馆是国内领先的专业模特写真摄影作品分享平台，致力于为摄影爱好者和模特行业关注者提供高质量的白露写真作品、风格解析与拍摄技巧参考。",
        },
      },
      {
        "@type": "Question",
        name: "如何获取白露的最新写真作品？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "您可以关注我们的网站首页，我们会持续更新白露最新的写真作品。同时也可以通过微信公众号（bailu_model_official）和微博（@国模白露写真馆）获取最新动态。",
        },
      },
      {
        "@type": "Question",
        name: "网站上的内容可以转载吗？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "本站所有作品均受版权保护，未经授权禁止转载。如果您希望使用本网站的内容，请通过邮箱 contact@bailu-model.com 与我们联系获取授权。",
        },
      },
      {
        "@type": "Question",
        name: "如何与白露合作拍摄？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "如有商业合作或拍摄需求，欢迎通过邮箱或微信与我们联系。我们的团队会评估合作方案并在3个工作日内回复。",
        },
      },
    ],
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.contact.email,
    contactPoint: {
      "@type": "ContactPoint",
      email: siteInfo.contact.email,
      contactType: "customer service",
    },
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [orgSchema, faqSchema],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={siteInfo.brandIntro.substring(0, 150)}
        keywords={`关于我们,品牌介绍,联系方式,${siteInfo.keywords}`}
        canonicalUrl={pageUrl}
        ogType="website"
        schemaData={combinedSchema}
      />

      <div className="pt-20 pb-12 md:pb-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "关于我们" },
            ]}
          />

          <div className="mt-6 mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
              关于我们
            </h1>
          </div>

          {/* Brand Introduction */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground-950 mb-4">
              品牌介绍
            </h2>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {siteInfo.brandIntro}
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-4">
                我们的平台汇集了白露从业多年来的经典写真作品，涵盖清新、时尚、古典、黑白等多种风格。每一组作品都经过精心挑选，旨在为摄影爱好者提供高质量的视觉参考和创作灵感。同时，我们还分享专业的拍摄技巧、后期处理教程以及模特行业的相关知识，帮助更多摄影爱好者提升自己的技术水平。
              </p>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground-950 mb-4">
              口碑评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {siteInfo.reputation.map((item, i) => (
                <div
                  key={i}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3 italic">
                    "{item.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-user-line text-secondary-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{item.author}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground-950 mb-4">
              联系我们
            </h2>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-mail-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</p>
                    <p className="text-sm text-foreground-600">{siteInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-wechat-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-1">微信公众号</p>
                    <p className="text-sm text-foreground-600">{siteInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-weibo-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-1">微博</p>
                    <p className="text-sm text-foreground-600">{siteInfo.contact.weibo}</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-background-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-1">办公地址</p>
                    <p className="text-sm text-foreground-600">{siteInfo.contact.address}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground-950 mb-4">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqSchema.mainEntity.map((faq, i) => (
                <details
                  key={i}
                  className="bg-background-50 border border-background-200/70 rounded-lg group"
                >
                  <summary className="p-5 flex items-center justify-between cursor-pointer text-sm font-medium text-foreground-800">
                    {faq.name}
                    <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                    <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}